<template>
	<div class="resultados_tab report_theme_container" data-theme="resultados">
		<TabHeader
			title="Estado de resultados"
			description="Visualiza el estado de tu negocio en un solo lugar."
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			:compact="true"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange" />

		<!-- Table Section -->
		<div class="underHeader">
			<FinancialResultsTable
				class="mt-4"
				:rows="tableRows"
				:headers="getHeadersTable"
				:isLoading="isLoading"
				:error="error || ''"
				:filterPeriod="filterPeriod"
				:expandedRows="expandedRows"
				:show-period-select="false"
				:cell-tooltips="true"
				fill-height
				navigable
				@toggle-childs="toggleRow"
				@navigate-row="onNavigateRow"
				@navigate-cell="onNavigateCell" />
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";

import TabHeader from "@/components/financialReports/TabHeader.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";
import { TABS_WITH_DRILL } from "@/views/financialReports/config/tabs";

// El tab destino de cada fila viene en la estructura del backend
// (`targetTab`: el grupo de la cuenta, o Rentabilidad para los márgenes y los
// resultados calculados). Click en una fila salta a su tab; las filas de
// Ventas/Costos/Gastos además abren el modal del mes (esos tabs tienen drill,
// `TABS_WITH_DRILL`, compartido con el Dashboard).

// Cada línea del P&L que es una cuenta (Ventas, Costos, Gastos, No
// Operacional) lleva chevron y se abre en sus cuentas de nivel 3; las filas
// calculadas (márgenes, EBITDA, utilidad) no. Parten plegadas.
//
// Filas hijas por cuenta: `acc:<key de la línea>:<cuenta L3>`. Tanto la
// key (`l2:<id>` para cuentas fuera de las canónicas) como el nombre de la
// cuenta pueden llevar ":", así que la línea padre se resuelve buscando la
// key conocida más larga que encabece el resto del id.
const ACCOUNT_ROW_PREFIX = "acc:";

// Las líneas que ya van sangradas (hijas de un total, tema crema) bajan sus
// cuentas L3 un escalón más de sangría para leerse bajo su padre.
function accountThemeFor(row) {
	return row && row.parentKey
		? "theme_row_account is-nested"
		: "theme_row_account";
}

function parentKeyOf(rowId, headers) {
	if (!rowId.startsWith(ACCOUNT_ROW_PREFIX)) return rowId;
	const rest = rowId.slice(ACCOUNT_ROW_PREFIX.length);
	return Object.keys(headers)
		.filter((key) => rest.startsWith(`${key}:`))
		.sort((a, b) => b.length - a.length)[0] || rest.split(":")[0];
}

export default {
	name: "ResultadosTab",
	components: {
		TabHeader,
		FinancialResultsTable
	},
	data() {
		return {
			// Totales con hijas desplegadas (Gastos Totales, No Operacional,
			// Ventas Netas con varias cuentas): la visibilidad de las hijas
			// vive en el store (TOGGLE_CHILD_ROWS por padre).
			expandedParents: [],
			// Líneas del P&L abiertas en sus cuentas L3 (estado local).
			expandedAccountRows: [],
			filterOptions: FILTER_PERIOD_OPTIONS
		};
	},
	computed: {
		...mapState("reportsFilters", ["dataFirstMonth", "endMonth", "selectedPeriodRange", "customRange", "customYearRange"]),
		...mapState("financialReports", [
			"filterPeriod",
			"tableRowHeaders",
			"netSellsDetail",
			"maxCostsDetail",
			"maxExpensesDetail",
			"accountsDetail",
			"isLoading",
			"error"
		]),
		...mapGetters("financialReports", {
			getHeadersTableFunc: "getHeadersTable"
		}),
		getHeadersTable() {
			return this.getHeadersTableFunc();
		},
		tableRows() {
			const headers = this.tableRowHeaders;
			const rows = [];
			Object.entries(headers)
				// Sólo las filas que el backend marcó para este tipo de empresa
				// (all_keys → isShowed, en el fetch). El estado inicial y los
				// fetch fallidos no traen la marca: sin datos no se pinta
				// NINGUNA fila. Antes (`!== false`) un timeout de /state/
				// dejaba la estructura default completa — una empresa de
				// Servicios veía las filas de Productos.
				.filter(([, row]) => row.isShowed === true)
				.forEach(([key, row]) => {
					// `targetTab` (de la estructura) habilita la afordancia de
					// navegación por fila en la tabla (null = no navegable).
					const targetTab = row.targetTab || null;
					const accounts = this.accountsDetail[key] || [];
					rows.push({
						...row,
						id: key,
						targetTab,
						hasChilds: Boolean(row.hasChilds) || accounts.length > 0,
					});
					if (!this.expandedAccountRows.includes(key)) return;
					// Cuentas L3 de la línea: mismos dicts por cabecera que las
					// líneas (los arma el store), así celdas y tooltips no
					// distinguen padre de hija. Heredan el tab destino del padre.
					// Tema de cuenta (gris, no crema): se diferencian de las
					// hijas de Gastos totales, que son líneas del P&L.
					const accountTheme = accountThemeFor(row);
					accounts.forEach((account) => {
						rows.push({
							id: `${ACCOUNT_ROW_PREFIX}${key}:${account.label}`,
							label: account.label,
							theme: accountTheme,
							isMoney: true,
							itemsDict: account.itemsDict,
							prevYearDict: account.prevYearDict,
							prevPeriodDict: account.prevPeriodDict,
							targetTab,
						});
					});
				});
			return rows;
		},
		expandedRows() {
			return [...this.expandedParents, ...this.expandedAccountRows];
		},
		getNow() {
			return moment().toDate();
		},
		// Rango activo del calendar del header (modo range, igual que Ventas). Si
		// el store tiene `customRange` lo usamos tal cual; si no, derivamos un
		// default (últimos 12 meses sobre endMonth) para que el picker abra
		// pintando "Último año" en vez de "—". Es sólo visual: el fetch sigue
		// usando selectedPeriodRange hasta que el usuario elija un rango.
		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},
		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
		// abra pintando el span real y nunca difiera de la tabla.
		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},
		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción. Incluye customRange
		// y customYearRange para refetchear cuando el usuario mueve el rango.
		fetchSignature() {
			return [
				this.filterPeriod,
				this.endMonth,
				this.selectedPeriodRange,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},
	watch: {
		fetchSignature() {
			this.fetchReportData();
		},
	},
	mounted() {
		this.fetchReportData();
	},
	methods: {
		...mapActions("financialReports", ["fetchReportData"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH", "SET_PENDING_DRILL"]),
		...mapMutations("financialReports", [
			"SET_FILTER_PERIOD",
			"TOGGLE_CHILD_ROWS"
		]),
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		// El calendar del header (modo range) emite el rango elegido; la action
		// sincroniza customRange + selectedPeriodRange="custom" + endMonth.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// El picker de Anual emite el rango de años; estado ortogonal al mes
		// (no toca endMonth/selectedPeriodRange).
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
		// Chevron de una fila. Un total con hijas (Gastos Totales, No
		// Operacional, Ventas Netas con varias cuentas) despliega líneas del
		// P&L cuya visibilidad vive en el store; el resto son líneas abiertas
		// en sus cuentas L3, estado local.
		toggleRow(rowId) {
			const row = this.tableRowHeaders[rowId];
			if (row && row.hasChilds) {
				const isExpanded = !this.expandedParents.includes(rowId);
				this.expandedParents = isExpanded
					? [...this.expandedParents, rowId]
					: this.expandedParents.filter((id) => id !== rowId);
				this.TOGGLE_CHILD_ROWS({ parentKey: rowId, isExpanded });
				return;
			}
			const idx = this.expandedAccountRows.indexOf(rowId);
			if (idx === -1) {
				this.expandedAccountRows = [...this.expandedAccountRows, rowId];
			} else {
				this.expandedAccountRows = this.expandedAccountRows.filter((id) => id !== rowId);
			}
		},
		// Cambia el tab activo vía query param (la URL es la fuente de verdad del
		// tab activo; DashboardReports lee $route.query.tab). No-op si ya estamos
		// en ese tab.
		goToTab(tab) {
			if (this.$route.query.tab === tab) return;
			this.$router
				.replace({ query: { ...this.$route.query, tab } })
				.catch((err) => { if (err.name !== "NavigationDuplicated") throw err; });
		},
		// Click en el nombre de una fila → sólo salta al tab de esa fila. Una
		// cuenta L3 navega como su línea padre.
		targetTabOf(rowId) {
			const row = this.tableRowHeaders[parentKeyOf(rowId, this.tableRowHeaders)];
			return (row && row.targetTab) || null;
		},
		onNavigateRow({ rowId }) {
			const tab = this.targetTabOf(rowId);
			if (tab) this.goToTab(tab);
		},
		// Click en una celda de valor → salta al tab y, si ese tab tiene modal de
		// drill (Ventas/Costos) y estamos en granularidad mensual, deja un
		// pendingDrill { tab, monthLabel } que el tab destino consume al montar
		// para abrir el modal de ese mes. En trimestral/anual la celda agrupa
		// varios meses, así que sólo navega.
		onNavigateCell({ rowId, monthLabel }) {
			const tab = this.targetTabOf(rowId);
			if (!tab) return;
			if (this.filterPeriod === "month" && TABS_WITH_DRILL.includes(tab)) {
				this.SET_PENDING_DRILL({ tab, monthLabel });
			}
			this.goToTab(tab);
		},
	},
};
</script>

<style scoped lang="scss">
// El tab puede medir COMO MÁXIMO lo que queda de ventana bajo el chrome:
// 100vh menos el header global (--app-chrome-top) y el padding-bottom de 30px
// que DashboardReports (.main_div) deja bajo todos los tabs. Columna flex:
// el header ocupa lo suyo y la tabla (fill-height) se encoge hasta el resto y
// scrollea adentro; con pocas filas mide lo que su contenido, como siempre.
// Resultado: la vista nunca genera el scroll vertical del navegador — con un
// tope fijo de filas la suma dependía de la pantalla y en 1080p sobraban
// unos píxeles que mostraban la barra y escondían las cabeceras.
// Sin padding inferior propio: el respiro bajo la card es el padding-bottom
// de 30px de `.main_div`, que ya está descontado del tope. Un padding extra
// acá era espacio en blanco fijo bajo una tabla recortada (Steve, 2026-10-07).
.resultados_tab {
	position: relative;
	display: flex;
	flex-direction: column;
	max-height: calc(100vh - var(--app-chrome-top, 56px) - 30px);
}

.underHeader {
	flex: 0 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
}
</style>
